import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ETLayout } from "@/pages/et/ETLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Save, Building2, MapPin, Plus, Trash2 } from "lucide-react";

export function ETConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/et/config").then(({ data }) => setCfg({ org_name: "", iban: "", causale: "", logo_url: "", stamp_url: "", signature_url: "", responsabile: "", due_day: 10, receipt_prefix: "", sender_email: "", ...data })); }, []);
  const save = async () => {
    try { await api.put("/et/config", cfg); toast.success("Configurazione salvata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!cfg) return <ETLayout title="Configurazione"><div /></ETLayout>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  return (
    <ETLayout subtitle="Amministrazione" title="Configurazione">
      <Tabs defaultValue="org">
        <TabsList className="bg-white border border-slate-200 mb-6">
          <TabsTrigger value="org" className="gap-2"><Building2 className="h-4 w-4" /> Organizzazione</TabsTrigger>
          <TabsTrigger value="centri" className="gap-2"><MapPin className="h-4 w-4" /> Centri servizio</TabsTrigger>
        </TabsList>
        <TabsContent value="org">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 max-w-2xl grid grid-cols-2 gap-4">
            <Fld label="Nome organizzazione"><Input value={cfg.org_name} onChange={(e) => set("org_name", e.target.value)} data-testid="et-cfg-org" /></Fld>
            <Fld label="Responsabile"><Input value={cfg.responsabile} onChange={(e) => set("responsabile", e.target.value)} /></Fld>
            <Fld label="Email mittente (Anffas — per invii automatici)"><Input type="email" value={cfg.sender_email} onChange={(e) => set("sender_email", e.target.value)} placeholder="segreteria@anffas…" data-testid="et-cfg-sender" /></Fld>
            <Fld label="IBAN"><Input value={cfg.iban} onChange={(e) => set("iban", e.target.value)} data-testid="et-cfg-iban" /></Fld>
            <Fld label="Causale bonifico"><Input value={cfg.causale} onChange={(e) => set("causale", e.target.value)} /></Fld>
            <Fld label="Prefisso numero ricevuta"><Input value={cfg.receipt_prefix} onChange={(e) => set("receipt_prefix", e.target.value)} placeholder="Es. R-" /></Fld>
            <Fld label="Giorno scadenza quota"><Input type="number" value={cfg.due_day} onChange={(e) => set("due_day", Number(e.target.value))} /></Fld>
            <Fld label="URL logo (per ricevute/solleciti)"><Input value={cfg.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…" /></Fld>
            <Fld label="URL timbro"><Input value={cfg.stamp_url} onChange={(e) => set("stamp_url", e.target.value)} placeholder="https://…" /></Fld>
            <Fld label="URL firma responsabile"><Input value={cfg.signature_url} onChange={(e) => set("signature_url", e.target.value)} placeholder="https://…" /></Fld>
            <div className="col-span-2"><Button onClick={save} className="brand-gradient text-white border-0" data-testid="et-cfg-save"><Save className="h-4 w-4 mr-2" /> Salva configurazione</Button></div>
          </div>
        </TabsContent>
        <TabsContent value="centri"><CentriPanel /></TabsContent>
      </Tabs>
    </ETLayout>
  );
}

function CentriPanel() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/et/centri").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name) return; try { await api.post("/et/centri", { name, active: true }); setName(""); load(); toast.success("Centro aggiunto"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const del = async (c) => { try { await api.delete(`/et/centri/${c.centro_id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 max-w-2xl">
      <div className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nuovo centro servizio…" data-testid="et-centro-input" />
        <Button onClick={add} className="brand-gradient text-white border-0" data-testid="et-centro-add"><Plus className="h-4 w-4" /></Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Centro</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.centro_id}>
              <TableCell className="text-slate-900">{c.name}</TableCell>
              <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => del(c)}><Trash2 className="h-4 w-4" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function ETLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/et/logs").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; } };
  return (
    <ETLayout subtitle="Amministrazione" title="Registro operazioni">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Data/Ora</TableHead><TableHead>Utente</TableHead><TableHead>Operazione</TableHead><TableHead>Precedente</TableHead><TableHead>Nuovo</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">Nessuna operazione registrata.</TableCell></TableRow> :
              items.map((l) => (
                <TableRow key={l.log_id}>
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmt(l.at)}</TableCell>
                  <TableCell className="text-sm">{l.utente}</TableCell>
                  <TableCell className="text-sm text-slate-900">{l.operazione}</TableCell>
                  <TableCell className="text-sm text-slate-500">{l.valore_precedente || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{l.nuovo_valore || "—"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </ETLayout>
  );
}

const Fld = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-slate-600 text-sm">{label}</Label>{children}</div>);
